import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import * as operationalReportsApi from '@sophocles/api/operational-reports';

// Entity adapter for normalized state management
// Note: No sortComparer - sorting is handled by the server via the 'o' parameter
const operationalReportsAdapter = createEntityAdapter({
  selectId: report => report.id,
});

// Async thunks for operational reports
export const fetchOperationalReports = createAsyncThunk(
  'operationalReports/fetchOperationalReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await operationalReportsApi.fetchOperationalReports(params);
    } catch (error) {
      // Extract more useful error message
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load reports';
      return rejectWithValue(message);
    }
  },
);

export const fetchOperationalReport = createAsyncThunk('operationalReports/fetchOperationalReport', async reportId => {
  return await operationalReportsApi.fetchOperationalReport(reportId);
});

export const fetchOperationalReportsSummary = createAsyncThunk(
  'operationalReports/fetchOperationalReportsSummary',
  async (params = {}) => {
    return await operationalReportsApi.fetchOperationalReportsSummary(params);
  },
);

// Initial state
const initialState = {
  reports: operationalReportsAdapter.getInitialState({
    loading: false,
    error: null,
    count: 0,
    next: null,
    previous: null,
  }),
  currentReport: null,
  currentReportLoading: false,
  currentReportError: null,
  summary: {
    data: null,
    loading: false,
    error: null,
  },
  filters: {
    site: null,
    site_name: '',
    status: [],
    validation_mode: '',
    report_date: null,
    date_range_start: null,
    date_range_end: null,
    text_search: '',
    load_id: null,
  },
  pagination: {
    page: 1,
    limit: 50,
    offset: 0,
  },
  sorting: {
    sortBy: 'report_date',
    sortDesc: true,
  },
};

// Slice
const operationalReportsSlice = createSlice({
  name: 'operationalReports',
  initialState,
  reducers: {
    // Filter actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    resetFilters: state => {
      // Preserve the site filter when resetting (important for site-specific views)
      const currentSite = state.filters.site;
      state.filters = { ...initialState.filters, site: currentSite };
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setSiteFilter: (state, action) => {
      state.filters.site = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setSiteNameFilter: (state, action) => {
      state.filters.site_name = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setDateRangeFilter: (state, action) => {
      const { start, end } = action.payload;
      state.filters.date_range_start = start;
      state.filters.date_range_end = end;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setTextSearchFilter: (state, action) => {
      state.filters.text_search = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setLoadIdFilter: (state, action) => {
      state.filters.load_id = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },

    // Pagination actions
    setPage: (state, action) => {
      state.pagination.page = action.payload;
      state.pagination.offset = (action.payload - 1) * state.pagination.limit;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },

    // Sorting actions
    setSortBy: (state, action) => {
      state.sorting.sortBy = action.payload;
    },
    setSortDesc: (state, action) => {
      state.sorting.sortDesc = action.payload;
    },

    // Clear current report
    clearCurrentReport: state => {
      state.currentReport = null;
      state.currentReportError = null;
    },

    // Clear error
    clearError: state => {
      state.reports.error = null;
      state.currentReportError = null;
      state.summary.error = null;
    },

    // Reset reports (clear all and start fresh)
    resetReports: state => {
      operationalReportsAdapter.removeAll(state.reports);
      state.reports.count = 0;
      state.reports.next = null;
      state.reports.previous = null;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch reports list
      .addCase(fetchOperationalReports.pending, state => {
        state.reports.loading = true;
        state.reports.error = null;
      })
      .addCase(fetchOperationalReports.fulfilled, (state, action) => {
        state.reports.loading = false;
        const { results = [], count = 0, next = null, previous = null } = action.payload;

        // If offset is 0, replace all (fresh load). Otherwise, append (infinite scroll)
        if (state.pagination.offset === 0) {
          operationalReportsAdapter.setAll(state.reports, results);
        } else {
          operationalReportsAdapter.addMany(state.reports, results);
        }

        state.reports.count = count;
        state.reports.next = next;
        state.reports.previous = previous;
      })
      .addCase(fetchOperationalReports.rejected, (state, action) => {
        state.reports.loading = false;
        state.reports.error = action.payload || action.error.message;
      })

      // Fetch single report
      .addCase(fetchOperationalReport.pending, state => {
        state.currentReportLoading = true;
        state.currentReportError = null;
      })
      .addCase(fetchOperationalReport.fulfilled, (state, action) => {
        state.currentReportLoading = false;
        state.currentReport = action.payload;
        // Also update in the normalized state if it exists
        operationalReportsAdapter.upsertOne(state.reports, action.payload);
      })
      .addCase(fetchOperationalReport.rejected, (state, action) => {
        state.currentReportLoading = false;
        state.currentReportError = action.error.message;
      })

      // Fetch summary
      .addCase(fetchOperationalReportsSummary.pending, state => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(fetchOperationalReportsSummary.fulfilled, (state, action) => {
        state.summary.loading = false;
        state.summary.data = action.payload;
      })
      .addCase(fetchOperationalReportsSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.error.message;
      });
  },
});

// Export actions
export const {
  setFilters,
  resetFilters,
  setSiteFilter,
  setSiteNameFilter,
  setStatusFilter,
  setDateRangeFilter,
  setTextSearchFilter,
  setLoadIdFilter,
  setPage,
  setLimit,
  setSortBy,
  setSortDesc,
  clearCurrentReport,
  clearError,
  resetReports,
} = operationalReportsSlice.actions;

// Selectors
const reportsSelectors = operationalReportsAdapter.getSelectors(state => state.operationalReports.reports);

// Basic selectors
export const selectAllReports = reportsSelectors.selectAll;
export const selectReportById = reportsSelectors.selectById;
export const selectReportsLoading = state => state.operationalReports.reports.loading;
export const selectReportsError = state => state.operationalReports.reports.error;
export const selectReportsCount = state => state.operationalReports.reports.count;
export const selectReportsNext = state => state.operationalReports.reports.next;
export const selectReportsPrevious = state => state.operationalReports.reports.previous;

export const selectCurrentReport = state => state.operationalReports.currentReport;
export const selectCurrentReportLoading = state => state.operationalReports.currentReportLoading;
export const selectCurrentReportError = state => state.operationalReports.currentReportError;

export const selectSummary = state => state.operationalReports.summary.data;
export const selectSummaryLoading = state => state.operationalReports.summary.loading;
export const selectSummaryError = state => state.operationalReports.summary.error;

export const selectFilters = state => state.operationalReports.filters;
export const selectPagination = state => state.operationalReports.pagination;
export const selectSorting = state => state.operationalReports.sorting;

// Computed selectors
export const selectHasMorePages = state => {
  const count = selectReportsCount(state);
  const { page, limit } = selectPagination(state);
  return page * limit < count;
};

export const selectTotalPages = state => {
  const count = selectReportsCount(state);
  const { limit } = selectPagination(state);
  return Math.ceil(count / limit);
};

export default operationalReportsSlice.reducer;
